import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { encryptPassword } from '../../../services/auth/authApi';
import { getDepartments, getUniversities } from '../../../services/personApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import { apiClient } from '../../../services/admin/apiClient';

interface FormData {
  email: string;
  name: string;
  department: string;
  birth: string;
  phone: string;
  encryptedPassword: string;
  identity: 'STUDENT' | 'DOCTOR' | 'OTHER' | '';
  memo: string;
}

// 서버 PersonAuthRequestValidator.PASSWORD_REGEX 와 같은 규칙
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,16}$/;
const PASSWORD_RULE_MESSAGE = '비밀번호는 대소문자, 숫자를 포함하여 8~16자리여야 합니다.';

const AccountCreateComponent: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    name: '',
    department: '',
    birth: '',
    phone: '',
    encryptedPassword: '',
    identity: '',
    memo: '',
  });
  const [passwordValue, setPasswordValue] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAffiliations = useCallback(async (identity: 'STUDENT' | 'DOCTOR') => {
    try {
      const affiliationList = identity === 'STUDENT' ? await getUniversities() : await getDepartments();
      setAffiliations(affiliationList);
    } catch (err) {
      setAffiliations([]);
    }
  }, []);

  const handleIdentityChange = (identity: 'STUDENT' | 'DOCTOR' | 'OTHER' | '') => {
    setFormData((prev) => ({ ...prev, identity, department: '' }));

    if (identity === 'STUDENT' || identity === 'DOCTOR') {
      void fetchAffiliations(identity);
    } else {
      setAffiliations([]);
    }
  };

  React.useEffect(() => {
    if (birthYear && birthMonth && birthDay) {
      const year = birthYear.length === 2 ? `20${birthYear}` : birthYear;
      const month = birthMonth.padStart(2, '0');
      const day = birthDay.padStart(2, '0');
      setFormData((prev) => ({ ...prev, birth: `${year}-${month}-${day}` }));
    }
  }, [birthYear, birthMonth, birthDay]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handlePasswordChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasswordValue(value);

    if (!value) {
      setFormData((prev) => ({ ...prev, encryptedPassword: '' }));
      return;
    }

    try {
      const encrypted = await encryptPassword(value);
      setFormData((prev) => ({ ...prev, encryptedPassword: encrypted }));
    } catch (err) {
      alert('비밀번호 암호화에 실패했습니다.');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      alert('이름을 입력해주세요.');
      return;
    }
    if (!formData.identity) {
      alert('회원 분류를 선택해주세요.');
      return;
    }
    if ((formData.identity === 'STUDENT' || formData.identity === 'DOCTOR') && !formData.department) {
      alert('소속을 선택해주세요.');
      return;
    }
    if (formData.identity === 'OTHER' && !formData.memo.trim()) {
      alert('비고를 입력해주세요.');
      return;
    }
    if (!formData.birth) {
      alert('생년월일을 입력해주세요.');
      return;
    }
    if (!formData.phone) {
      alert('전화번호를 입력해주세요.');
      return;
    }
    if (!formData.email) {
      alert('이메일을 입력해주세요.');
      return;
    }
    if (!passwordValue) {
      alert('임시 비밀번호를 입력해주세요.');
      return;
    }
    if (!PASSWORD_REGEX.test(passwordValue)) {
      alert(PASSWORD_RULE_MESSAGE);
      return;
    }
    if (!formData.encryptedPassword) {
      alert('비밀번호 암호화에 실패했습니다. 다시 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiClient.post('/api/admin/accounts', {
        email: formData.email,
        name: formData.name,
        department:
          formData.identity === 'STUDENT' || formData.identity === 'DOCTOR'
            ? formData.department
            : undefined,
        birth: formData.birth,
        phone: formData.phone.replace(/-/g, ''),
        encryptedPassword: formData.encryptedPassword,
        identity: formData.identity,
        memo: formData.memo.trim() || undefined,
      });

      alert('회원 계정이 성공적으로 생성되었습니다.');

      setFormData({
        email: '',
        name: '',
        department: '',
        birth: '',
        phone: '',
        encryptedPassword: '',
        identity: '',
        memo: '',
      });
      setPasswordValue('');
      setBirthYear('');
      setBirthMonth('');
      setBirthDay('');
      setAffiliations([]);
    } catch (err: any) {
      const errorMessage = err?.status
        ? `[ ${err.status} ] 회원 생성에 실패했습니다. ${err.message}`
        : `회원 생성에 실패했습니다. ${err?.message ?? ''}`.trim();
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>회원 계정 생성</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        <FormSection>
          <FieldRow>
            <FieldGroup>
              <FieldLabel>이름</FieldLabel>
              <InputField
                type="text"
                placeholder="이름을 입력해주세요."
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </FieldGroup>
          </FieldRow>

          <FieldRow>
            <FieldGroup>
              <FieldLabel>생년월일</FieldLabel>
              <BirthSelects>
                <SelectField value={birthYear} onChange={(e) => setBirthYear(e.target.value)}>
                  <option value="">년</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </SelectField>
                <SelectField value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)}>
                  <option value="">월</option>
                  {monthOptions.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </SelectField>
                <SelectField value={birthDay} onChange={(e) => setBirthDay(e.target.value)}>
                  <option value="">일</option>
                  {dayOptions.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </SelectField>
              </BirthSelects>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>전화번호</FieldLabel>
              <InputField
                type="text"
                placeholder="010-1234-5678"
                value={formData.phone}
                onChange={handlePhoneChange}
                maxLength={13}
              />
            </FieldGroup>
          </FieldRow>

          <FieldRow>
            <FieldGroup>
              <FieldLabel>이메일</FieldLabel>
              <InputField
                type="email"
                placeholder="이메일을 입력해주세요."
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                autoComplete="off"
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>임시 비밀번호</FieldLabel>
              <InputField
                type="password"
                placeholder="대소문자, 숫자 포함 8~16자"
                value={passwordValue}
                onChange={handlePasswordChange}
                autoComplete="new-password"
              />
            </FieldGroup>
          </FieldRow>

          <FieldRow>
            <FieldGroup>
              <FieldLabel>회원 분류</FieldLabel>
              <SelectField
                value={formData.identity}
                onChange={(e) => handleIdentityChange(e.target.value as FormData['identity'])}
              >
                <option value="">회원 분류를 선택해주세요.</option>
                <option value="STUDENT">학생</option>
                <option value="DOCTOR">의사</option>
                <option value="OTHER">기타</option>
              </SelectField>
            </FieldGroup>
            {(formData.identity === 'STUDENT' || formData.identity === 'DOCTOR') && (
              <FieldGroup>
                <FieldLabel>소속</FieldLabel>
                <SelectField
                  value={formData.department}
                  onChange={(e) => setFormData((prev) => ({ ...prev, department: e.target.value }))}
                  disabled={affiliations.length === 0}
                >
                  <option value="">소속을 선택해주세요.</option>
                  {affiliations.map((affiliation) => (
                    <option key={affiliation} value={affiliation}>
                      {affiliation}
                    </option>
                  ))}
                </SelectField>
              </FieldGroup>
            )}
          </FieldRow>

          <FieldRow>
            <FieldGroupFull>
              <FieldLabel>비고{formData.identity === 'OTHER' ? ' *' : ''}</FieldLabel>
              <InputField
                type="text"
                placeholder={
                  formData.identity === 'OTHER'
                    ? '비고를 필수로 입력해주세요.'
                    : '내용을 입력해주세요.'
                }
                value={formData.memo}
                onChange={(e) => setFormData((prev) => ({ ...prev, memo: e.target.value }))}
              />
            </FieldGroupFull>
          </FieldRow>
        </FormSection>

        <ButtonContainer>
          <CreateButton type="button" onClick={handleSubmit} disabled={loading}>
            {loading ? '생성 중...' : '계정 생성하기'}
          </CreateButton>
        </ButtonContainer>
        {error ? <ErrorText>{error}</ErrorText> : null}
      </AdminSection>
    </AdminPage>
  );
};

const FormSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const FieldGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldGroupFull = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;


const BirthSelects = styled.div`
  display: flex;
  gap: 8px;

  > * {
    flex: 1;
  }
`;

const FieldLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: var(--font-family-base);
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const InputField = styled.input`
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  font-size: 16px;
  font-family: var(--font-family-base);
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  transition: border-color 0.2s;
  height: 48px;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:focus {
    border-color: #448181;
  }
`;

const SelectField = styled.select`
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  font-size: 16px;
  font-family: var(--font-family-base);
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;
  height: 48px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.46 5.13L8 10.67L13.54 5.13' stroke='rgba(55, 56, 60, 0.61)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;

  &:disabled {
    background-color: rgba(0, 0, 0, 0.05);
    cursor: not-allowed;
    color: rgba(55, 56, 60, 0.28);
  }

  &:focus {
    border-color: #448181;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const CreateButton = styled.button`
  padding: 12px 28px;
  background: #448181;
  border-radius: 12px;
  border: none;
  color: #fff;
  font-size: 16px;
  font-family: var(--font-family-base);
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #3a6d6d;
  }

  &:disabled {
    background: rgba(68, 129, 129, 0.5);
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #e52222;
  font-size: 14px;
`;

export default AccountCreateComponent;
